import {
  ACCESS_TOKEN,
  AxiosInstance,
  DEVICE_ID,
  REFRESH_TOKEN,
} from "api/axiosInstance";
import {
  ProfileAccountInfo,
  TEMPORARY_ACCESS_STATUS,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { UserInfo } from "api/interfaces/userInterface.interface";
import { getItemFromLocalStorage, useLocalStorage } from "hook/useLocalStorage";
import {
  useState,
  createContext,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  purgeAccount,
  setAccountSetting,
} from "redux/reducers/account/accountSlice";
import { getCookie, removeCookie, setCookie } from "cookies/cookie";
import { getUserDetail, getUserDetailAccount } from "api/userAPI";
import { setMode } from "redux/reducers/theme/themeReducer";
import {
  setIsAuth,
  setIsLogout,
  setResetAuth,
} from "redux/reducers/auth/isAuth";
import { VERIFY_TYPE } from "pages/auth/verify2fa/resendCode/resendCode";
import { getSwitchAccountRoleId, logoutAbleUrl } from "utils/MenuUtil";
import {
  defaultDiscountTier,
  checkIsSuspended,
  isHiddenGroup,
} from "utils/AccountUtil";
import { shutdown as shutdownChat } from "@intercom/messenger-js-sdk";
import { checkEmpty, checkMaintenance, getSearchEmail } from "utils/functions";
import { setThemeClass } from "styles/theme";
import useApiError from "hook/useApiError";
import {
  connectStomp,
  disconnectStomp,
  setCurrentUser,
} from "redux/stomp/stompSlice";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { clearAxiosHeaders, setAxiosHeaders } from "utils/AuthUtil";
import { LastPath } from "api/interfaces/authInterface.interface";
import { useQueryClient } from "react-query";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";
import qs from "qs";

function queryStringAfterFirstQuestionMark(decoded: string): string {
  const i = decoded.indexOf("?");
  return i === -1 ? decoded : decoded.slice(i + 1);
}

const checkRoleId = (accountId: string) => {
  let roleId = "";
  const lastSession: LastPath = JSON.parse(
    sessionStorage.getItem("userType") || "{}"
  );
  if (lastSession.accountId === accountId) {
    console.log(lastSession.userType, "lastSession.userType");
    roleId = lastSession.userType as string;
  }
  const ut: LastPath = JSON.parse(
    localStorage.getItem("ut") as string
  ) as LastPath;
  if (ut.accountId === accountId) {
    roleId = ut.userType as string;
  }
  return roleId;
};

type LoginFunc = (user: UserInfo, isHidden: boolean) => void;
type UpdateProfileImageUrl = (url: string) => void;
type UpdateUserInfo = (user: UserInfo) => void;
type UpdateVerify2fa = (verify2fa: boolean) => void;
type LogoutFunc = () => void;
type ProcessLinkNavigate = ({
  toPathParam,
  toSearchParam,
  userInfo,
  roleId,
  loginAccountId,
}: {
  toPathParam: string;
  toSearchParam: URLSearchParams;
  userInfo: UserInfo;
  roleId?: string;
  loginAccountId?: string;
}) => void;
type LoginNavigator = (
  toPathParam: string,
  userInfo: UserInfo,
  isHidden: boolean
) => void;

export const initialUserData: UserInfo = {
  userId: "",
  email: "",
  name: "",
  profileImg: "",
  isAccount: 0,
  totalAccount: 0,
  startPage: 1,
  theme: "dark",
  verify2fa: true,
  use2faCode: VERIFY_TYPE.EMAIL,
  userLevel: LEVEL_TYPE.INTL,
  userKey: "",
  botInfo: "",
  level: [],
  accounts: {
    accountId: "",
    accountName: "",
    accountLevel: LEVEL_TYPE.INTL,
    accountNumber: "",
    accountAdditionalNumber: "",
    parentAccountId: "",
    parentLevel: "",
    timezone: "",
    dateFormat: "",
    timeFormat: "",
    companyName: "",
    companyLocation: "",
    companyLogo: "",
    use2faAuth: false,
    userPermission: [],
    recorderPermission: [],
    description: "",
    isHidden: false,
    isSuspended: 0,
    isFavorite: false,
    isCreator: false,
    createdAt: "",
    invitedOwner: false,
    creatorName: "",
    creatorEmail: "",
    isDecide: 0,
    discountTier: defaultDiscountTier,
    cascadedSuspension: 0,
    isTemporaryAccess: TEMPORARY_ACCESS_STATUS.NO,
    temporaryAccessDuration: 0,
    restrictList: [],
    billingType: BILLING_TYPE.UNKNOWN,
    isPayment: false,
    cycle: BILLING_CYCLE.MONTHLY,
    planActiveChannel: 0,
  },
};

const AuthContext = createContext<{
  user: UserInfo;
  login: LoginFunc;
  logout: LogoutFunc;
  updateUserInfo: UpdateUserInfo;
  updateVerify2fa: UpdateVerify2fa;
  updateProfileImageUrl: UpdateProfileImageUrl;
  processLinkNavigate: ProcessLinkNavigate;
  updateTotalAccount: (count: number) => void;
  loginNavigator: LoginNavigator;
  onForwardLogin: () => void;
  showIntercom: boolean;
  setShowIntercom: (show: boolean) => void;
}>({
  user: initialUserData,
  login: () => {},
  logout: () => {},
  updateUserInfo: () => {},
  updateVerify2fa: () => {},
  updateProfileImageUrl: () => {},
  processLinkNavigate: () => {},
  updateTotalAccount: () => {},
  loginNavigator: () => {},
  onForwardLogin: () => {},
  showIntercom: false,
  setShowIntercom: () => {},
});

// TODO 새로고침 후에도 로그인 유지하기 위한 전역관리 필요. Token도 같은 문제.
export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [user, setUser] = useState<UserInfo>(initialUserData);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [, setUserLogin] = useLocalStorage("users", "");
  const { lang } = useApiError();
  const queryClient = useQueryClient();
  const [showIntercom, setShowIntercom] = useState<boolean>(false);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const onForwardLogin = () => {
    clearAxiosHeaders();
    if (getCookie(ACCESS_TOKEN)) {
      navigate("/login", { replace: true });
    } else {
      logout();
    }
    dispatch(setResetAuth());
  };
  const processLinkNavigate = ({
    toPathParam,
    toSearchParam,
    userInfo,
    roleId,
    loginAccountId,
  }: {
    toPathParam: string;
    toSearchParam: URLSearchParams;
    userInfo: UserInfo;
    roleId?: string;
    loginAccountId?: string;
  }) => {
    if (!toPathParam) {
      toPathParam = "/";
    }
    const goToPath = (path: string, accountId: string) => {
      navigate(
        {
          pathname: path,
          search: accountId ? `?account=${accountId}&r=${roleId}` : "",
        },
        {
          replace: true,
        }
      );
    };
    let accountId: string = "";
    if (
      toSearchParam &&
      (toSearchParam?.get("accountId") || toSearchParam?.get("account"))
    ) {
      accountId =
        toSearchParam.get("accountId") ||
        (toSearchParam.get("account") as string);
    }
    if (!accountId) {
      accountId =
        loginAccountId ??
        selectedAccount.accountId ??
        userInfo.accounts.accountId;
    }
    if (
      toPathParam?.includes("alert") &&
      toSearchParam &&
      toSearchParam?.get("recorderId")
    ) {
      const recorderId = toSearchParam.get("recorderId") as string;
      const historyId = toSearchParam.get("historyId") as string;
      navigate(
        {
          pathname: `/alert/history/detail`,
          search: `?account=${accountId}&recorderId=${recorderId}${
            historyId ? `&historyId=${historyId}` : ""
          }&r=${roleId}`,
        },
        {
          replace: true,
        }
      );
    } else if (toPathParam?.includes("report")) {
      goToPath(toPathParam, accountId);
    } else if (toPathParam?.includes("videoShare")) {
      goToPath(toPathParam, accountId);
    } else if (toPathParam?.includes("subscription")) {
      const { accountLevel } = userInfo.accounts;
      let toPath = "/subscription/organization";
      console.log(accountLevel, "accountLevel");
      console.log(toPathParam, "toPathParam");
      if (accountLevel === LEVEL_TYPE.CP) {
        toPath = "/subscription/keys/organization";
      }
      if (toPathParam.includes("history")) {
        toPath = toPath + "/history";
      }
      goToPath(toPath, accountId);
    } else {
      goToPath(toPathParam, accountId);
    }
  };

  const loginNavigator = (
    toPathParam: string,
    userInfo: UserInfo,
    isHidden: boolean
  ) => {
    login(userInfo, isHidden);
    navigate(
      {
        pathname: toPathParam,
        search: `?account=${userInfo.accounts.accountId}&r=${
          isHidden
            ? localStorage.getItem("r-hid")
            : localStorage.getItem("r-mem")
        }`,
      },
      {
        replace: true,
      }
    );
  };

  //조직 변경시 유저 레벨 변경
  useEffect(() => {
    //user에 속한 그룹레벨을 유지/변경
    setUser((prevUser) => ({
      ...prevUser,
      userLevel:
        selectedAccount.userPermission.groupName?.toUpperCase() === "HIDDEN"
          ? prevUser.userLevel
          : selectedAccount.accountLevel,
    }));
  }, [selectedAccount]);

  useEffect(() => {
    const loadUser = async () => {
      const maintenance = await checkMaintenance();
      if (maintenance?.isActive) {
        setCookie({
          name: "maintenance",
          value: maintenance.description,
        });
        window.location.href = "/maintenance.html";
        return;
      }
      return !!getCookie(ACCESS_TOKEN) &&
        !!localStorage.getItem("r-mem") &&
        !!localStorage.getItem("r-hid")
        ? getItemFromLocalStorage("users")
        : undefined;
      //return await localStorage.getItem("users");
    };

    const searchParams = new URLSearchParams(window.location.search);
    const searchCode = searchParams.get("code");
    const mobilePlusCode = searchParams.get("mobilePlusCode");
    const hasCode = !!searchCode || !!mobilePlusCode;
    clearAxiosHeaders();
    if (hasCode) {
      setIsAuthLoading(false);
      return;
    }

    const decodeLocation = decodeURIComponent(
      pathname.includes("?") || pathname.includes("%3F") ? pathname : search
    );
    const toSearch = queryStringAfterFirstQuestionMark(decodeLocation);
    const toSearchParam = new URLSearchParams(toSearch);
    const toPathParam = toSearchParam.get("url") ?? pathname;
    const email = getSearchEmail(window.location);

    const failToLogin = () => {
      dispatch(setIsLogout());
      shutdownChat();
      setUser(initialUserData);
      dispatch(disconnectStomp());
      localStorage.removeItem("users");
      clearAuth();
      navigate("/login", {
        state: {
          pathname: pathname,
          search: toSearch,
        },
        replace: true,
      });
      setIsAuthLoading(false);
    };
    if (!logoutAbleUrl.includes(pathname)) {
      loadUser()
        .then((user) => {
          const loadUser = user ? (JSON.parse(user) as UserInfo) : undefined;
          //console.log("user information load from localStorage");
          const linkEmail = email;
          let accountId =
            toSearchParam.get("accountId") ||
            (toSearchParam.get("account") as string);
          if (!accountId) {
            accountId = selectedAccount.accountId;
          }
          let roleId = "";

          if (toSearchParam.get("r")) {
            if (toSearchParam.get("r") === localStorage.getItem("r-hid")) {
              roleId = localStorage.getItem("r-hid") as string;
            } else if (
              toSearchParam.get("r") === localStorage.getItem("r-mem")
            ) {
              roleId = localStorage.getItem("r-mem") as string;
            }
          }
          if (accountId && !roleId) {
            roleId =
              checkRoleId(accountId) ??
              (localStorage.getItem("r-mem") as string);
          }
          let isHidden = roleId === localStorage.getItem("r-hid");
          if (loadUser && linkEmail && linkEmail === loadUser.email) {
            //링크이메일과 로그인 아이디가 같은 경우
            getUserDetailAccount(accountId)
              .then((response) => {
                //Account Setting 후 Redirect
                if (
                  response.accounts !== undefined &&
                  checkIsSuspended(response.accounts) !==
                    SUSPENSION_STATUS.DISABLED
                ) {
                  if (isHiddenGroup(response.accounts.userPermission)) {
                    isHidden = true;
                    roleId = localStorage.getItem("r-hid") as string;
                  }
                  login(response, isHidden);
                  if (toSearchParam.get("url") !== null) {
                    processLinkNavigate({
                      toPathParam: toSearchParam.get("url") as string,
                      toSearchParam,
                      userInfo: loadUser,
                      roleId: roleId as string,
                      loginAccountId: response.accounts.accountId,
                    });
                  } else {
                    //링크이메일이 아닌경우
                    loginNavigator(toPathParam, response, isHidden);
                  }
                  setIsAuthLoading(false);
                } else {
                  // notify(
                  //   "error",
                  //   intl.formatMessage({
                  //     id: "label.auth.link.notAvailable",
                  //     defaultMessage: "The linked information is no longer valid.",
                  //   })
                  // );
                  navigate("/error");
                  setIsAuthLoading(false);
                }
              })
              .catch((error) => {
                failToLogin();
              });
          } else if (!linkEmail && loadUser) {
            //링크이메일이 아니고
            //로컬에 유저정보가 있고
            //토큰이 있는 경우
            //device-id가 이상하거나, 토큰 만료 검사를 위해
            //무조건 유저정보 재조회
            const checkAccountToLogin = async (accountId: string) => {
              let roleId =
                checkRoleId(accountId) ??
                (localStorage.getItem("r-mem") as string);
              let isHidden = roleId === localStorage.getItem("r-hid");
              const toSearchParam = new URLSearchParams(window.location.search);
              toSearchParam.set("account", accountId);
              toSearchParam.set("r", roleId);
              return await getUserDetailAccount(accountId)
                .then((accountResponse) => {
                  if (
                    accountResponse.accounts &&
                    checkIsSuspended(accountResponse.accounts) !==
                      SUSPENSION_STATUS.DISABLED
                  ) {
                    if (
                      isHiddenGroup(accountResponse.accounts.userPermission)
                    ) {
                      isHidden = true;
                      roleId = localStorage.getItem("r-hid") as string;
                    }
                    login(accountResponse, isHidden);
                    if (toPathParam === "/" || toPathParam === "/login") {
                      loginNavigator(toPathParam, accountResponse, isHidden);
                    } else {
                      navigate(
                        {
                          pathname: pathname,
                          search: toSearchParam.toString(),
                        },
                        {
                          replace: true,
                        }
                      );
                    }
                    setIsAuthLoading(false);
                  } else {
                    failToLogin();
                    throw new Error(lang.ServerErrorCode[3001]);
                  }
                })
                .catch((error) => {
                  failToLogin();
                  throw error;
                });
            };
            getUserDetail()
              .then((response) => {
                if (!accountId) {
                  accountId =
                    toSearchParam.get("account") ||
                    toSearchParam.get("accountId") ||
                    selectedAccount.accountId ||
                    response.accounts.accountId;
                }
                if (accountId) {
                  //주소창에 account=가 있거나
                  //selectedAccount.accountId가 있거나
                  //response.accounts.accountId가 있으면
                  //현재 토큰과 device-id가 해당 org접근권한이 있는지 조회
                  checkAccountToLogin(accountId).catch((error) => {
                    if (response.accounts.accountId !== accountId) {
                      //주소창에있는 accountId의 권한이 없고 response.accounts.accountId의 권한이 있으면
                      checkAccountToLogin(response.accounts.accountId).catch(
                        (error) => {
                          //주소창에있는 accountId의 권한이 없고 response.accounts.accountId의 권한이 없으면
                          failToLogin();
                        }
                      );
                    } else {
                      failToLogin();
                    }
                  });
                } else {
                  //주소창에 account=가 없고
                  //selectedAccount.accountId가 없고
                  //response.accounts.accountId가 없으면
                  //로그인 페이지로 이동
                  failToLogin();
                }
              })
              .catch((error) => {
                //유저정보 실패
                //로그인 페이지로 이동
                failToLogin();
              });
          } else {
            //유저정보가 없고
            //토큰도 없을때
            //얘는 axios의 헤더가 delete이기때문에 리셋안함
            failToLogin();
          }
        })
        .catch((error) => {
          failToLogin();
        });
    } else {
      clearAxiosHeaders();
      setIsAuthLoading(false);
    }
  }, []);

  const updateProfileImageUrl = (url: string) => {
    const updateObject = Object.assign({}, user);
    updateObject.profileImg = url;

    setUser((user) => {
      return { ...user, profileImg: url as string };
    });
    setUserLogin(JSON.stringify(updateObject));
  };

  const updateUserInfo = (user: UserInfo) => {
    setUser(user);
    setUserLogin(JSON.stringify(user));
    setThemeClass(user.theme);
    dispatch(setMode(user.theme));
  };

  const updateVerify2fa = (verify2fa: boolean) => {
    setUser((us) => {
      return {
        ...us,
        verify2fa,
      };
    });
  };

  const updateTotalAccount = (count: number) => {
    setUser((user) => {
      return { ...user, totalAccount: count };
    });
  };

  const login: LoginFunc = async (user: UserInfo, isHidden: boolean) => {
    queryClient.clear();
    dispatch(setMode(user.theme));
    dispatch(setResetAuth());
    setThemeClass(user.theme);
    // setUserGA(user.email);
    localStorage.setItem("botInfo", user.botInfo);
    setUser(user);
    setUserLogin(JSON.stringify(user));
    const filterAccount = {
      ...user.accounts,
      userPermission: user.accounts.userPermission.filter((permission) =>
        isHidden
          ? permission.groupName?.toUpperCase() === "HIDDEN"
          : permission.groupName?.toUpperCase() !== "HIDDEN"
      ),
    };
    dispatch(setAccountSetting(filterAccount));
    dispatch(connectStomp(user));
    dispatch(setCurrentUser(user));
    // dispatch(
    //   clearPushBreadcrumbRoute({
    //     name: user.accounts.accountName,
    //     accountId: user.accounts.accountId,
    //   })
    // );
  };

  const delay = (milliseconds: number) => {
    return new Promise((resolve) => {
      setTimeout(resolve, milliseconds);
    });
  };
  const clearAuth = () => {
    sessionStorage.clear();
    dispatch(purgeAccount("PURGE"));
    clearAxiosHeaders();
    removeCookie(ACCESS_TOKEN);
    removeCookie(REFRESH_TOKEN);
    localStorage.removeItem(DEVICE_ID);
    localStorage.removeItem("r-mem");
    localStorage.removeItem("r-hid");
  };
  const logout: LogoutFunc = async () => {
    dispatch(setIsLogout());
    shutdownChat();
    setUser(initialUserData);
    dispatch(disconnectStomp());
    localStorage.removeItem("users");
    setShowIntercom(false);
    if (!pathname.includes("/signup")) {
      await delay(400);
      navigate("/login");
    }
    if (
      selectedAccount.accountId &&
      getCookie(ACCESS_TOKEN) &&
      getCookie(REFRESH_TOKEN)
    ) {
      try {
        await AxiosInstance.get(`/auth/logout/${selectedAccount.accountId}`);
      } catch (error) {
        clearAuth();
      }
    }
    clearAuth();
  };


  const changeStorageEvent = useCallback(
    async (ev: StorageEvent) => {
      if (ev.key === "users" && ev.newValue) {
        const change = JSON.parse(
          JSON.parse(ev.newValue || "") || ""
        ) as UserInfo;
        if (
          change !== undefined &&
          change.email !== undefined &&
          user.email !== ""
        ) {
          if (change.email !== user.email) {
            setUser(initialUserData);
            console.log("changeStorageEvent", "isAuth");
            window.dataLayer?.push({
              event: "expired",
              reason: "storage_event",
              page: window.location.pathname,
            });
            dispatch(setIsAuth(false));
            await dispatch(purgeAccount("PURGE"));
            navigate("/login", { replace: true });
            return;
          } 
          if (getCookie(ACCESS_TOKEN)) {
            setAxiosHeaders({
              accessToken: getCookie(ACCESS_TOKEN) as string,
                roleId: getSwitchAccountRoleId(selectedAccount.isHidden),
              deviceId: localStorage.getItem(DEVICE_ID) as string,
            });
            return;
          }
          logout();
          
        }
      }
    },
    [dispatch, logout, navigate, selectedAccount.isHidden, user.email]
  );

  // 로컬스토리지 이벤트 리스너 등록
  useEffect(() => {
    (() => {
      window.addEventListener("storage", changeStorageEvent);
    })();
    return () => {
      window.removeEventListener("storage", changeStorageEvent);
    };
  }, [changeStorageEvent]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUserInfo,
        updateVerify2fa,
        updateProfileImageUrl,
        updateTotalAccount,
        processLinkNavigate,
        loginNavigator,
        onForwardLogin,
        showIntercom,
        setShowIntercom,
      }}
    >
      {/* <LogoutChannel /> */}
      {!isAuthLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
